import moment from 'moment'

export const WEEKDAY_OPTIONS = [
    { label: 'Segunda', value: 1 },
    { label: 'Terça', value: 2 },
    { label: 'Quarta', value: 3 },
    { label: 'Quinta', value: 4 },
    { label: 'Sexta', value: 5 },
    { label: 'Sábado', value: 6 },
    { label: 'Domingo', value: 0 }
]

const WEEKDAY_SHORT = {
    0: 'Dom',
    1: 'Seg',
    2: 'Ter',
    3: 'Qua',
    4: 'Qui',
    5: 'Sex',
    6: 'Sáb'
}

const DEFAULT_DAYS = [1, 2, 3, 4, 5]
const DEFAULT_TIME = moment('18:00', 'HH:mm')

const normalizeDay = (day) => {
    const value = Number(day)
    if (value === 7) return 0
    return value
}

const expandDayToken = (token) => {
    const trimmed = String(token || '').trim()
    if (!trimmed || trimmed === '*') {
        return [0, 1, 2, 3, 4, 5, 6]
    }

    if (trimmed.includes('-')) {
        const [startRaw, endRaw] = trimmed.split('-')
        let start = normalizeDay(startRaw)
        let end = normalizeDay(endRaw)
        if (Number.isNaN(start) || Number.isNaN(end)) return []

        // Ex.: 5-1 (sex–dom) atravessa a semana
        const days = []
        let current = start
        for (let i = 0; i < 7; i += 1) {
            days.push(current)
            if (current === end) break
            current = (current + 1) % 7
        }
        return days
    }

    const day = normalizeDay(trimmed)
    return Number.isNaN(day) ? [] : [day]
}

const parseDaysField = (daysField) => {
    if (!daysField || daysField === '*') {
        return [0, 1, 2, 3, 4, 5, 6]
    }

    const unique = new Set()
    String(daysField)
        .split(',')
        .forEach((token) => {
            expandDayToken(token).forEach((day) => unique.add(day))
        })

    return Array.from(unique).sort((a, b) => {
        const orderA = a === 0 ? 7 : a
        const orderB = b === 0 ? 7 : b
        return orderA - orderB
    })
}

const formatDaysForCron = (days = []) => {
    const unique = [...new Set(days.map(normalizeDay))].filter((day) => day >= 0 && day <= 6)
    if (!unique.length) return '1-5'
    if (unique.length === 7) return '*'

    const ordered = unique.sort((a, b) => a - b)

    // Faixa contínua sem atravessar domingo→segunda (exceto lista completa)
    let isRange = ordered.length > 1
    for (let i = 1; i < ordered.length; i += 1) {
        if (ordered[i] !== ordered[i - 1] + 1) {
            isRange = false
            break
        }
    }

    if (isRange) {
        return `${ordered[0]}-${ordered[ordered.length - 1]}`
    }

    return ordered.join(',')
}

export const parseCronToSchedule = (cron) => {
    const parts = String(cron || '').trim().split(/\s+/)
    if (parts.length < 5) {
        return {
            dias_semana: DEFAULT_DAYS,
            horario: DEFAULT_TIME.clone()
        }
    }

    const minute = Number(parts[0])
    const hour = Number(parts[1])
    const days = parseDaysField(parts[4])

    const safeMinute = Number.isNaN(minute) ? 0 : Math.min(Math.max(minute, 0), 59)
    const safeHour = Number.isNaN(hour) ? 18 : Math.min(Math.max(hour, 0), 23)

    return {
        dias_semana: days.length ? days : DEFAULT_DAYS,
        horario: moment({ hour: safeHour, minute: safeMinute })
    }
}

export const buildCronFromSchedule = (diasSemana, horario) => {
    const time = moment.isMoment(horario) ? horario : moment(horario)
    const minute = time.isValid() ? time.minute() : 0
    const hour = time.isValid() ? time.hour() : 18
    const daysField = formatDaysForCron(diasSemana)

    return `${minute} ${hour} * * ${daysField}`
}

export const formatCronLabel = (cron) => {
    const { dias_semana, horario } = parseCronToSchedule(cron)
    if (!dias_semana?.length || !horario?.isValid?.()) {
        return cron || '-'
    }

    const timeLabel = horario.format('HH:mm')
    const ordered = [...dias_semana].sort((a, b) => {
        const orderA = a === 0 ? 7 : a
        const orderB = b === 0 ? 7 : b
        return orderA - orderB
    })

    let daysLabel
    if (ordered.length === 7) {
        daysLabel = 'Todos os dias'
    } else {
        const sequential = ordered.every((day, index) => {
            if (index === 0) return true
            const prev = ordered[index - 1]
            const prevOrder = prev === 0 ? 7 : prev
            const dayOrder = day === 0 ? 7 : day
            return dayOrder === prevOrder + 1
        })

        if (sequential && ordered.length > 1) {
            daysLabel = `${WEEKDAY_SHORT[ordered[0]]}–${WEEKDAY_SHORT[ordered[ordered.length - 1]]}`
        } else {
            daysLabel = ordered.map((day) => WEEKDAY_SHORT[day]).join(', ')
        }
    }

    return `${daysLabel} às ${timeLabel}`
}

export const DEFAULT_CRON_SCHEDULE = {
    dias_semana: DEFAULT_DAYS,
    horario: DEFAULT_TIME
}
